import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Info, Download } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { MENU } from "../data/menu";
import { IMAGES, waLink } from "../lib/site";
import { Reveal } from "../components/Reveal";

const PageHeader = ({ kicker, title, image }) => (
  <section className="relative pt-40 md:pt-52 pb-16 md:pb-20 overflow-hidden" data-testid="page-header">
    <div className="absolute inset-0 opacity-25">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy-deep/60 via-navy/80 to-navy" />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="flex items-center gap-4 mb-6">
        <span className="h-px w-12 bg-gold/60" />
        <span className="font-body text-xs tracking-[0.3em] uppercase text-gold">{kicker}</span>
      </motion.div>
      <h1 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95]">
        {title}
      </h1>
    </div>
  </section>
);

export default function MenuPage() {
  const { t, lang } = useLang();
  const [active, setActive] = useState(MENU[0].id);

  useEffect(() => {
    const onScroll = () => {
      for (const cat of MENU) {
        const el = document.getElementById(`cat-${cat.id}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 160 && rect.bottom >= 160) {
            setActive(cat.id);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(`cat-${id}`);
    if (el) window.scrollTo({ top: el.offsetTop - 130, behavior: "smooth" });
  };

  return (
    <main data-testid="menu-page">
      <PageHeader kicker={t("menu.kicker")} title={t("menu.title")} image={IMAGES.heroPizza} />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24">
        <Reveal>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-12">
            <div className="flex items-start gap-3 border border-gold/25 bg-gold/5 px-5 py-4 max-w-3xl flex-1">
              <Info size={18} className="text-gold mt-0.5 shrink-0" />
              <p className="font-body text-sm text-cream/70 leading-relaxed">{t("menu.note")}</p>
            </div>
            <a
              href="/menu-mamma-rosa.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download
              data-testid="menu-download-btn"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-navy-deep font-body text-sm font-semibold tracking-wide transition-transform hover:scale-[1.03] whitespace-nowrap shrink-0"
            >
              <Download size={17} />
              {t("menu.download")}
            </a>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-[220px_1fr] gap-10 lg:gap-16">
          {/* Sticky category nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-1" data-testid="menu-nav">
              {MENU.map((cat) => (
                <button
                  key={cat.id}
                  data-testid={`menu-nav-${cat.id}`}
                  onClick={() => scrollTo(cat.id)}
                  className={`block w-full text-left font-body text-sm py-2 transition-colors ${
                    active === cat.id ? "text-gold" : "text-cream/50 hover:text-cream"
                  }`}
                >
                  {cat.name[lang] || cat.name.en}
                </button>
              ))}
            </div>
          </aside>

          {/* Mobile chips */}
          <div className="lg:hidden -mx-5 px-5 overflow-x-auto flex gap-2 pb-2 sticky top-[76px] z-40 bg-navy/90 backdrop-blur-md py-3" data-testid="menu-chips">
            {MENU.map((cat) => (
              <button
                key={cat.id}
                onClick={() => scrollTo(cat.id)}
                className={`whitespace-nowrap rounded-full border px-4 py-1.5 font-body text-xs ${
                  active === cat.id ? "border-gold text-gold" : "border-white/15 text-cream/60"
                }`}
              >
                {cat.name[lang] || cat.name.en}
              </button>
            ))}
          </div>

          <div className="space-y-20">
            {MENU.map((cat) => (
              <section key={cat.id} id={`cat-${cat.id}`} data-testid={`menu-cat-${cat.id}`}>
                <div className="flex items-baseline justify-between border-b border-white/10 pb-4 mb-8">
                  <h2 className="font-display text-3xl md:text-4xl tracking-tight text-cream">{cat.name[lang] || cat.name.en}</h2>
                  {cat.example && (
                    <span className="font-body text-[10px] tracking-[0.2em] uppercase text-cream/35">indicativo</span>
                  )}
                </div>
                <div className="space-y-7">
                  {cat.items.map((it, j) => (
                    <Reveal key={j} delay={(j % 4) * 0.05}>
                      <div className="flex items-baseline gap-4">
                        <div className="flex-1">
                          <div className="flex items-baseline gap-3">
                            <h3 className="font-body text-base md:text-lg text-cream flex items-center gap-2">
                              {it.name}
                              {it.badge && (
                                <span className="text-[10px] tracking-widest uppercase bg-gold text-navy-deep px-2 py-0.5">{it.badge}</span>
                              )}
                            </h3>
                          </div>
                          {(it.desc[lang] || it.desc.en) && (it.desc[lang] || it.desc.en) !== "—" && (
                            <p className="mt-1 font-body text-sm text-cream/50 max-w-xl leading-relaxed">{it.desc[lang] || it.desc.en}</p>
                          )}
                        </div>
                        <span className="hidden sm:block flex-1 border-b border-dotted border-white/15 translate-y-[-4px]" />
                        <span className="font-display text-xl text-gold whitespace-nowrap">
                          {it.price} {t("menu.currency")}
                        </span>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>

        <Reveal>
          <div className="mt-20 text-center">
            <a
              href={waLink("Ciao Mamma Rosa! Vorrei prenotare un tavolo.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-book-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-navy-deep font-body font-semibold tracking-wide transition-transform hover:scale-[1.03]"
            >
              {t("common.bookWhatsapp")}
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
